import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { nonRelevantActivityClassificationIds } from '../../../../modules/activity_classifications';
import { requestGet } from '../../../../modules/request/index';
import { closedLockIcon, scheduledIcon, ENTER, ESCAPE } from '../../../Shared/index';
import ScorebookTooltip from './tooltip/scorebook_tooltip';
import gradeColor from '../modules/grade_color.js';

function skillDescription(grade, activityName = 'the activity') {
  if (grade == null) return `did not complete ${activityName}`;
  if (grade < 0.32) return `rarely demonstrated ${activityName}'s skill`;
  if (grade < 0.83) return `sometimes demonstrated ${activityName}'s skill`;
  if (grade <= 1.0) return `frequently demonstrated ${activityName}'s skill`;
  return `did not complete ${activityName}`;
}

function activityFromClassificationId(classificationId) {
  const id = Number(classificationId);
  switch (id) {
    case 1: return 'proofreader';
    case 2: return 'grammar';
    case 4: return 'diagnostic';
    case 5: return 'connect';
    case 6: return 'lessons';
    case 9: return 'evidence';
    default: return undefined;
  }
}

function activityIconDescription(classificationId) {
  const id = Number(classificationId);
  switch (id) {
    case 1: return 'Flag representing Quill Proofreader';
    case 2: return 'Puzzle piece representing Quill Grammar';
    case 4: return 'Magnifying glass representing Quill Diagnostic';
    case 5: return 'Bullseye representing Quill Connect';
    case 6: return 'Apple representing Quill Lessons ';
    case 9: return 'Book to represent Quill Reading for Evidence';
    default: return 'Icon';
  }
}

export const ActivityIconWithTooltip = ({ data, premium_state, context }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);

  const classId = useMemo(() => {
    const id =
      data?.activity_classification_id ??
      data?.classification?.id ??
      data?.activity?.classification?.id ??
      null;
    return id == null ? null : Number(id);
  }, [data]);

  const activityId = data.activityId;
  const tooltipId = useMemo(
    () => `${data.userId}-${activityId}-${data.cuId}`,
    [data.userId, activityId, data.cuId]
  );

  const indicateCompletedButUngradedActivity = useMemo(() => {
    return (
      context === 'scorebook' &&
      (nonRelevantActivityClassificationIds.includes(Number(classId)) || data.percentage === null)
    );
  }, [context, classId, data.percentage]);

  const altText = useMemo(() => {
    const activityDesc = activityIconDescription(classId);
    if (data.completed_attempts > 0 || data.percentage) {
      if (indicateCompletedButUngradedActivity) {
        return `${activityDesc} in dark blue to indicate that ${data.studentName} completed ${data.name}.`;
      }
      const pct = parseFloat(data.percentage);
      return `${activityDesc} in ${gradeColor(pct)} to indicate that ${data.studentName} ${skillDescription(pct, data.name)}`;
    } else {
      if (data.started) {
        return `${activityDesc} in light blue to indicate that ${data.studentName} started ${data.name}.`;
      }
      return `${activityDesc} in gray to indicate that ${data.studentName} has not yet started ${data.name}.`;
    }
  }, [
    classId,
    data.completed_attempts,
    data.percentage,
    data.studentName,
    data.name,
    data.started,
    indicateCompletedButUngradedActivity
  ]);

  const imageName = useMemo(() => {
    const base = activityFromClassificationId(classId);
    if (!base) return 'pages/activity_summary/unknown-grey';

    if (data.completed_attempts > 0 || data.percentage) {
      return `icons/s/tool-${base}-white`;
    }
    return data.started
      ? `pages/activity_summary/${base}-light-blue`
      : `pages/activity_summary/${base}-grey`;
  }, [classId, data.completed_attempts, data.percentage, data.started]);

  const iconClass = useMemo(() => {
    if (data.completed_attempts > 0 || data.percentage) {
      if (indicateCompletedButUngradedActivity) return 'completed-no-score';
      const pct = parseFloat(data.percentage);
      return gradeColor(pct);
    }
    return undefined;
  }, [data.completed_attempts, data.percentage, indicateCompletedButUngradedActivity]);

  const goToReport = useCallback(() => {
    window.location = `/teachers/progress_reports/report_from_classroom_unit_and_activity_and_user/cu/${data.cuId}/user/${data.userId}/a/${activityId}`;
  }, [data.cuId, data.userId, activityId]);

  const checkForStudentReport = useCallback(() => {
    if (data.completed_attempts) {
      goToReport();
    } else {
      alert('This activity has not been completed, so there is no report yet.');
    }
  }, [data.completed_attempts, goToReport]);

  const getConceptResultInfo = useCallback(() => {
    const url = `${window.postbuildEnv.DEFAULT_URL}/grades/tooltip/classroom_unit_id/${data.cuId}/user_id/${data.userId}/activity_id/${activityId}/completed/${!!data.completed_attempts}`;
    requestGet(url, (body) => {
      const merged = {
        ...data,
        premium_state,
        sessions: body?.sessions
      };
      setTooltipData(merged);
    });
  }, [data, premium_state, activityId]);

  const showTooltipAndMaybeFetch = useCallback(() => {
    setShowTooltip(true);
    if (!tooltipData) getConceptResultInfo();
  }, [tooltipData, getConceptResultInfo]);

  const hideTooltip = useCallback(() => setShowTooltip(false), []);

  const handleKeyDownOnIcon = useCallback(
    (e) => {
      if (e.key === ENTER && context === 'scorebook') {
        checkForStudentReport();
      }
    },
    [context, checkForStudentReport]
  );

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === ESCAPE) setShowTooltip(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const missedIndicator = useMemo(() => {
    const { marked_complete, completed_attempts } = data;
    if (marked_complete && completed_attempts === 0) {
      return (
        <img
          alt=""
          className="missed-indicator"
          src={`${window.postbuildEnv.CDN_URL}/images/scorebook/missed-lessons-cross.svg`}
        />
      );
    }
    return null;
  }, [data]);

  const statusIndicator = useMemo(() => {
    const { started, completed_attempts, scheduled, locked } = data;
    if (locked) {
      return <img alt="" className="locked-symbol" src={closedLockIcon.src} />;
    } else if (scheduled && completed_attempts < 1) {
      return <img alt="" className="scheduled-symbol" src={scheduledIcon.src} />;
    } else if (started) {
      return (
        <img
          alt=""
          className="in-progress-symbol"
          src="https://assets.quill.org/images/scorebook/blue-circle-sliced.svg"
        />
      );
    } else if (completed_attempts > 1) {
      const completedNumber = completed_attempts > 9 ? '+' : completed_attempts;
      return (
        <span>
          <img
            alt=""
            className="attempt-symbol"
            src="https://assets.quill.org/images/scorebook/blue-circle-solid.svg"
          />
          <span className="attempt-count">{completedNumber}</span>
        </span>
      );
    }
    return null;
  }, [data]);

  const tooltipClasses =
    'activate-tooltip icon-link icon-wrapper focus-on-light interactive-wrapper';
  const cursorType = context === 'scorebook' ? 'pointer' : 'default';
  const openTooltipFn = context === 'scorebook' ? showTooltipAndMaybeFetch : undefined;
  const hideTooltipFn = context === 'scorebook' ? hideTooltip : undefined;
  const onClickFn = context === 'scorebook' ? checkForStudentReport : undefined;

  return (
    <button
      aria-describedby={showTooltip ? tooltipId : undefined}
      className={tooltipClasses}
      onBlur={hideTooltipFn}
      onClick={onClickFn}
      onFocus={openTooltipFn}
      onKeyDown={handleKeyDownOnIcon}
      onMouseEnter={openTooltipFn}
      onMouseLeave={hideTooltipFn}
      role="link"
      style={{ position: 'relative', cursor: cursorType }}
      type="button"
    >
      <img
        alt={altText}
        className={iconClass}
        src={`${window.postbuildEnv.CDN_URL}/images/${imageName}.svg`}
      />
      {missedIndicator}
      {statusIndicator}
      {showTooltip && <ScorebookTooltip data={tooltipData || data} id={tooltipId} />}
    </button>
  );
}

export default ActivityIconWithTooltip
